import axios from 'axios';
import Cookies from 'universal-cookie';

export const SERVER = process.env.NODE_ENV === 'production'? process.env.REACT_APP_ENVIRONMENT === 'develop'? process.env.REACT_APP_DEV_SERVER: process.env.REACT_APP_PROD_SERVER : process.env.REACT_APP_LOCALHOST_SERVER

const server = axios.create({
    baseURL: `${SERVER}api/v1/`,
    timeout: 20000,
    timeoutErrorMessage: 'Servidor Instável. Tente mais tarde.',
    "Content-Type": "application/json",
});

/**
 * O access token dura 15 minutos e e renovado sozinho pelo refresh token, que
 * vale 6 meses. Quem cuida disso e o interceptor abaixo, entao nenhuma tela
 * precisa saber que a renovacao existe.
 *
 * Os cookies sao lidos e escritos aqui direto (fora do React) porque o refresh
 * pode acontecer no meio de qualquer request, sem passar por um componente.
 */
const cookies = new Cookies();

export const SESSION_MAX_AGE = 60 * 60 * 24 * 180; // 6 meses

const cookieOptions = (path = '/') => ({
    path,
    maxAge: SESSION_MAX_AGE,
    sameSite: 'lax',
    secure: window.location.protocol === 'https:',
});

/**
 * O login do backoffice grava os cookies em `/backoffice` e o do app em `/`.
 * Quem renova o token precisa gravar no MESMO path, senao o navegador fica com
 * dois cookies `token` e o de path mais longo (`/backoffice`, ja vencido)
 * ganha na leitura - a sessao morre em silencio 15 min depois do login.
 */
export const sessionCookiePath = () =>
    window.location.pathname.startsWith('/backoffice') ? '/backoffice' : '/';

export const getAccessToken = () => cookies.get('token');
export const getRefreshToken = () => cookies.get('refresh_token');

/**
 * Quem esta logado, segundo o token que esta no cookie.
 *
 * Le o payload sem conferir a assinatura, o que aqui basta: nao e decisao de
 * permissao, e so a resposta para "trocou de gente?". Quem valida de verdade e
 * o servidor, a cada requisicao e a cada conexao de socket.
 */
export const getTokenUserId = () => {
    const token = getAccessToken();
    if (!token) return null;

    try {
        const payload = token.split('.')[1];
        const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
        return JSON.parse(json)?.id ?? null;
    } catch (erro) {
        return null;
    }
};

// Quem precisa saber que o token mudou. O socket e o caso: ele se identifica
// no momento em que conecta, entao entrar numa conta depois que a conexao
// subiu deixaria a pessoa fora da propria sala ate um F5.
const ouvintesDeSessao = new Set();

export const onSessionChange = ouvinte => {
    ouvintesDeSessao.add(ouvinte);
    return () => ouvintesDeSessao.delete(ouvinte);
};

const avisarMudancaDeSessao = () => {
    ouvintesDeSessao.forEach(ouvinte => {
        try {
            ouvinte(getTokenUserId());
        } catch (erro) {
            // Um ouvinte quebrado nao pode impedir o login de terminar
            console.error('Falha ao avisar mudança de sessão:', erro.message);
        }
    });
};

export const setTokens = ({ token, refresh_token }, path = '/') => {
    if (token) cookies.set('token', token, cookieOptions(path));
    if (refresh_token) cookies.set('refresh_token', refresh_token, cookieOptions(path));
    avisarMudancaDeSessao();
};

export const clearTokens = () => {
    // Precisa do mesmo path usado na escrita, senao o cookie nao e removido
    cookies.remove('token', { path: '/' });
    cookies.remove('refresh_token', { path: '/' });
    cookies.remove('user', { path: '/' });
    cookies.remove('token', { path: '/backoffice' });
    cookies.remove('refresh_token', { path: '/backoffice' });
    cookies.remove('user', { path: '/backoffice' });
    avisarMudancaDeSessao();
};

server.interceptors.request.use(config => {
    // No reenvio pos-refresh o header ja veio com o token novo em maos. Reler o
    // cookie aqui sobrescreveria com o valor antigo e o retry tomaria 401 de novo.
    if (config._retried) return config;

    const token = getAccessToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
});

// Enquanto um refresh esta em andamento, as demais requisicoes que tomaram 401
// esperam nesta promise em vez de disparar um refresh cada uma. Sem isso, o
// primeiro refresh rotacionaria o token e os outros seriam recusados como
// reuso, derrubando a sessao inteira.
let refreshing = null;

const runRefresh = async () => {
    const refresh_token = getRefreshToken();
    if (!refresh_token) throw new Error('Sem refresh token');

    // Instancia limpa: a rota de refresh nao passa pelos interceptors
    const { data } = await axios.post(`${SERVER}api/v1/auth/refresh`, { refresh_token }, { timeout: 20000 });
    setTokens(data.data, sessionCookiePath());
    return data.data.token;
};

server.interceptors.response.use(
    response => response,
    async error => {
        const original = error.config;
        const status = error.response?.status;

        const isRefreshCall = original?.url?.includes('/auth/refresh');
        if (status !== 401 || !original || original._retried || isRefreshCall) {
            throw error;
        }

        if (!getRefreshToken()) throw error;

        original._retried = true;

        try {
            if (!refreshing) {
                refreshing = runRefresh().finally(() => {
                    refreshing = null;
                });
            }
            const token = await refreshing;
            original.headers.Authorization = `Bearer ${token}`;
            return server(original);
        } catch {
            // Refresh falhou: a sessao acabou de verdade. Limpar aqui garante
            // que o 401 que sobe para o useHandleErrors encontre o estado limpo.
            clearTokens();
            throw error;
        }
    },
);

export default server;
